import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Clock, Target, TrendingUp, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading'
import { DPSTimeline } from '@/components/charts/dps-timeline'
import { CastEfficiency } from '@/components/charts/cast-efficiency'
import { api, type Character, type Fight, type Report } from '@/lib/api'
import { formatNumber, formatPercentage } from '@/lib/utils'

interface AnalysisResultsProps {
  character: Character
  report: Report
  fight: Fight
  onBack: () => void
  className?: string
}

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  character,
  report,
  fight,
  onBack,
  className,
}) => {
  const generateAnalysisMutation = useMutation({
    mutationFn: () => api.generateAnalysis({
      reportCode: report.code,
      fightId: fight.id,
      characterName: character.name,
      realm: character.realm,
      region: character.region,
    }),
  })

  React.useEffect(() => {
    generateAnalysisMutation.mutate()
  }, [])

  if (generateAnalysisMutation.isPending) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Generating Analysis</h2>
            <p className="text-muted-foreground">
              Analyzing {character.name}'s performance on {fight.name}...
            </p>
          </div>
          <Button variant="outline" onClick={onBack}>
            Back to Reports
          </Button>
        </div>
        <LoadingState>
          This may take up to 30 seconds as we process the combat data and generate detailed insights...
        </LoadingState>
      </div>
    )
  }

  if (generateAnalysisMutation.isError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Analysis Failed</h2>
            <p className="text-muted-foreground">
              Failed to analyze {character.name}'s performance
            </p>
          </div>
          <Button variant="outline" onClick={onBack}>
            Back to Reports
          </Button>
        </div>
        <ErrorState
          title="Analysis Generation Failed"
          message={generateAnalysisMutation.error?.message || 'An unexpected error occurred'}
          onRetry={() => generateAnalysisMutation.mutate()}
        />
      </div>
    )
  }

  // Add check for successful data state
  if (!generateAnalysisMutation.data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Loading Analysis</h2>
            <p className="text-muted-foreground">
              Please wait while we process the data...
            </p>
          </div>
          <Button variant="outline" onClick={onBack}>
            Back to Reports
          </Button>
        </div>
        <LoadingState>
          Processing analysis data...
        </LoadingState>
      </div>
    )
  }

  const analysis = generateAnalysisMutation.data

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Performance Analysis</h2>
          <p className="text-muted-foreground">
            {character.name}'s analysis for {fight.name}
          </p>
        </div>
        <Button variant="outline" onClick={onBack}>
          Back to Reports
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <TrendingUp className="h-4 w-4 mr-2" />
              Overall DPS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(analysis.dpsAnalysis.overall)}
            </div>
            <p className="text-xs text-muted-foreground">
              {analysis.dpsAnalysis.percentile}th percentile
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Target className="h-4 w-4 mr-2" />
              Cast Efficiency
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercentage(
                Object.values(analysis.rotationAnalysis.castEfficiency)
                  .reduce((sum, spell) => sum + spell.efficiency, 0) /
                Object.keys(analysis.rotationAnalysis.castEfficiency).length
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Average across all abilities
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Downtime
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercentage(analysis.rotationAnalysis.downtime)}
            </div>
            <p className="text-xs text-muted-foreground">
              Time not casting
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Issues Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analysis.rotationAnalysis.mistakes.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Potential improvements
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <DPSTimeline data={analysis.dpsAnalysis.timeline} />
        <CastEfficiency data={analysis.rotationAnalysis.castEfficiency} />
      </div>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Target className="h-5 w-5 mr-2" />
            Performance Recommendations
          </CardTitle>
          <CardDescription>
            Prioritized suggestions to improve your performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analysis.recommendations.map((rec, index) => (
              <div 
                key={index} 
                className="flex items-start space-x-3 p-3 border rounded-lg"
              >
                <div className={`h-2 w-2 rounded-full mt-2 ${
                  rec.priority === 'high' ? 'bg-red-500' :
                  rec.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                }`} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{rec.category}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                      rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {rec.priority} priority
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {rec.description}
                  </p>
                  <p className="text-sm font-medium text-primary mt-2">
                    Expected Impact: {rec.impact}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Rotation Mistakes */}
      {analysis.rotationAnalysis.mistakes.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Rotation Issues
            </CardTitle>
            <CardDescription>
              Specific moments where improvements can be made
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analysis.rotationAnalysis.mistakes.map((mistake, index) => (
                <div 
                  key={index} 
                  className="flex items-start space-x-3 p-3 border rounded-lg"
                >
                  <div className={`h-2 w-2 rounded-full mt-2 ${
                    mistake.severity === 'high' ? 'bg-red-500' :
                    mistake.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm">
                        {Math.floor(mistake.timestamp / 1000)}s
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        mistake.severity === 'high' ? 'bg-red-100 text-red-800' :
                        mistake.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {mistake.severity}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{mistake.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}